import { Loader2, Send, RotateCcw, Check } from "lucide-react";
import { useRef, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type {
  AIGenerationChatMessage,
  AIGenerationConversation,
  AssignedWorkoutEditPreviewPayload,
  GptLogInputSnapshotRow,
  PhaseWorkoutPreviewPayload,
  ProgramCreationPreviewPayload,
  ProgramPhasePreviewPayload,
  SessionStructuresPreviewPayload,
} from "@/integrations/backend/program-generation";
import {
  parseAssignedWorkoutEditAssistantContent,
  parsePhaseWorkoutsAssistantContent,
  parseProgramCreationAssistantContent,
  parseProgramPhasesAssistantContent,
  parseSessionStructuresAssistantContent,
} from "@/integrations/backend/program-generation";
import { formatISODateLabelLong } from "@/lib/date-only";
import { labels } from "@/lib/onboarding-options";
import {
  parsePhaseDetailsBlocks,
  parseSessionStructureBlocks,
} from "@/lib/session-structure-format";
import {
  formatParseErrorForClipboard,
  lastAssistantRawOutputForCopy,
  lastUserInputForCopy,
} from "@/lib/format-error-for-copy";
import { ExpandableCopyDetails } from "./expandable-copy-details";
import { collectGptLogDebugInfo } from "./AIGenerationDebugSidebar";
import { GptLogDetailDialog, GptLogInputsDialog } from "./gpt-log-debug-dialogs";

function SessionStructureTables({ structure }: { structure: string }) {
  const blocks = parseSessionStructureBlocks(structure);
  if (!structure.trim()) {
    return <div className="text-xs text-gray-500">(empty structure)</div>;
  }
  return (
    <div className="space-y-3 text-sm">
      {blocks.map((block, idx) =>
        block.type === "text" ? (
          <div key={idx} className="whitespace-pre-wrap leading-relaxed">
            {block.content}
          </div>
        ) : (
          <div key={idx} className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full min-w-[280px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-3 py-2 font-semibold text-gray-700 w-8">#</th>
                  <th className="px-3 py-2 font-semibold text-gray-700">Session Flow</th>
                  <th className="px-3 py-2 font-semibold text-gray-700">Purpose</th>
                  <th className="px-3 py-2 font-semibold text-gray-700">Duration</th>
                </tr>
              </thead>
              <tbody className="text-gray-900">
                {block.rows.map((row, rIdx) => (
                  <tr key={rIdx} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2 text-gray-500">{row.num}.</td>
                    <td className="px-3 py-2 font-medium">{row.col1}</td>
                    <td className="px-3 py-2">{row.col2}</td>
                    <td className="px-3 py-2 text-gray-600">{row.col3}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function SessionStructuresFormattedContent({
  items,
}: {
  items: SessionStructuresPreviewPayload["session_structures"];
}) {
  return (
    <div className="space-y-5 text-sm">
      {items.map((item, idx) => (
        <div key={`${item.name}-${idx}`} className="space-y-2">
          <div className="font-semibold text-gray-900">
            {item.name}
            <span className="ml-2 font-normal text-gray-500">
              (Session {item.sort_order ?? idx + 1})
            </span>
          </div>
          <SessionStructureTables structure={item.structure} />
        </div>
      ))}
    </div>
  );
}

const PHASE_DETAILS_COLUMNS = [
  "Session Block",
  "Exercise / Series",
  "Sets × Reps / Tempo / Rest",
  "Coaching Focus / Notes",
] as const;

function ProgramPhasesFormattedContent({
  phases,
  phase1StartDate,
  showPhase1Start = true,
}: {
  phases: ProgramPhasePreviewPayload["phases"];
  phase1StartDate: string;
  showPhase1Start?: boolean;
}) {
  return (
    <div className="space-y-4 text-sm">
      {showPhase1Start && (
        <div className="text-xs text-gray-500 mb-2">
          Program start: {formatISODateLabelLong(phase1StartDate)}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {phases.map((phase, idx) => (
          <div
            key={`${phase.title}-${idx}`}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2"
          >
            <span className="font-medium text-gray-900">{phase.title}</span>
            <span className="mx-2 text-gray-400">•</span>
            <span className="text-gray-600">
              Weeks {phase.week_start}–{phase.week_end}
            </span>
            {phase.session_structure_name ? (
              <>
                <span className="mx-2 text-gray-400">•</span>
                <span className="text-gray-600">{phase.session_structure_name}</span>
              </>
            ) : null}
          </div>
        ))}
      </div>
      {phases.map((phase, phaseIdx) => {
        const detailsBlocks = parsePhaseDetailsBlocks(phase.details);

        return (
          <div key={phaseIdx} className="space-y-2">
            <div className="font-medium text-gray-900">
              {phase.title}
              <span className="ml-2 font-normal text-gray-600">
                (Weeks {phase.week_start}–{phase.week_end}
                {phase.session_structure_name
                  ? ` · ${phase.session_structure_name}`
                  : ""}
                )
              </span>
            </div>
            {(detailsBlocks.length > 0) ? (
              <div className="space-y-2">
                {detailsBlocks.map((block, idx) =>
                  block.type === "text" ? (
                    <div
                      key={idx}
                      className="whitespace-pre-wrap text-gray-700 text-xs"
                    >
                      {block.content}
                    </div>
                  ) : (
                    <div
                      key={idx}
                      className="overflow-x-auto rounded-lg border border-gray-200 bg-white"
                    >
                      <table className="w-full min-w-[320px] text-left text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="px-3 py-2 font-semibold text-gray-700 w-8">
                              #
                            </th>
                            {PHASE_DETAILS_COLUMNS.map((col) => (
                              <th
                                key={col}
                                className="px-3 py-2 font-semibold text-gray-700"
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="text-gray-900">
                          {block.rows.map((row, rIdx) => (
                            <tr
                              key={rIdx}
                              className="border-b border-gray-100 last:border-0"
                            >
                              <td className="px-3 py-2 text-gray-500">
                                {row.num}.
                              </td>
                              <td className="px-3 py-2 font-medium">
                                {row.col1}
                              </td>
                              <td className="px-3 py-2">{row.col2}</td>
                              <td className="px-3 py-2 text-gray-600">
                                {row.col3}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {row.col4}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-gray-600 text-xs rounded border border-gray-200 bg-gray-50 p-2">
                {phase.details || "(no details)"}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function WorkoutPreviewFormattedContent({
  workouts,
  exerciseNameMap,
}: {
  workouts: PhaseWorkoutPreviewPayload["workouts"];
  exerciseNameMap?: Record<string, string>;
}) {
  const displayName = (ex: { exercise_id: string; exercise_name?: string | null }) =>
    ex.exercise_name ?? exerciseNameMap?.[ex.exercise_id] ?? ex.exercise_id;

  return (
    <div className="space-y-3 text-sm">
      {workouts.map((workout, workoutIdx) => (
        <div key={`${workout.name}-${workoutIdx}`} className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">{workout.name}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-600">{workout.date}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-600">{workout.duration_minutes} min</span>
          </div>
          {workout.description ? (
            <p className="mt-1 text-xs text-gray-600 whitespace-pre-wrap">{workout.description}</p>
          ) : null}
          <div className="mt-2 space-y-2">
            {workout.groupings.map((grouping, groupingIdx) => (
              <div key={groupingIdx} className="rounded border border-gray-200 bg-gray-50 p-2">
                <div className="text-xs font-medium text-gray-700">
                  Group {groupingIdx + 1} ({grouping.exercises.length} exercises) - Sets {grouping.sets}, Rest {grouping.rest_seconds}s
                </div>
                <ul className="mt-1 text-xs text-gray-600 list-disc list-inside">
                  {grouping.exercises.map((exercise, exerciseIdx) => (
                    <li key={`${exercise.exercise_id}-${exerciseIdx}`}>
                      {displayName(exercise)}
                      {exercise.reps != null ? ` - ${exercise.reps} reps` : ""}
                      {exercise.time_seconds != null ? ` - ${exercise.time_seconds}s` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AssignedWorkoutEditFormattedContent({
  workout,
  exerciseNameMap,
}: {
  workout: AssignedWorkoutEditPreviewPayload;
  exerciseNameMap?: Record<string, string>;
}) {
  return (
    <WorkoutPreviewFormattedContent
      workouts={[
        {
          name: workout.name,
          date: workout.date,
          description: workout.description,
          duration_minutes: workout.duration_minutes,
          groupings: workout.groupings,
        },
      ]}
      exerciseNameMap={exerciseNameMap}
    />
  );
}

function ProgramCreationFormattedContent({
  program,
}: {
  program: ProgramCreationPreviewPayload;
}) {
  const motivationLabels = program.motivations.map((value) => labels.motivation[value] || value);
  const exerciseTypeLabels = program.exercise_types.map((value) => labels.exerciseType[value] || value);
  const detailEntries = Object.entries(program.exercise_type_details || {});

  return (
    <div className="space-y-3 text-sm">
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="text-lg font-semibold text-gray-900">{program.name}</div>
        <div className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{program.description}</div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Program details</div>
          <div className="mt-2 space-y-1 text-sm text-gray-700">
            <div>Duration: {program.duration} weeks</div>
            <div>Frequency: {program.frequency} sessions/week</div>
            <div>Difficulty: {program.difficulty}</div>
            <div>
              Weekly schedule: {program.weekly_schedule_preference?.trim() || "None provided"}
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Focus</div>
          <div className="mt-2 space-y-2 text-sm text-gray-700">
            <div>
              <span className="font-medium text-gray-900">Program focus:</span>{" "}
              {program.program_focus?.trim() || "None provided"}
            </div>
            <div>
              <span className="font-medium text-gray-900">What to expect:</span>{" "}
              {program.what_to_expect?.trim() || "None provided"}
            </div>
            <div>
              <span className="font-medium text-gray-900">Exercise variety:</span>{" "}
              {program.variety?.trim() || "None provided"}
            </div>
          </div>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Motivations</div>
          <div className="mt-2 text-sm text-gray-700">
            {motivationLabels.length > 0 ? motivationLabels.join(", ") : "None provided"}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Exercise types</div>
          <div className="mt-2 text-sm text-gray-700">
            {exerciseTypeLabels.length > 0 ? exerciseTypeLabels.join(", ") : "None provided"}
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Exercise type details</div>
        {detailEntries.length > 0 ? (
          <div className="mt-2 space-y-2 text-sm text-gray-700">
            {detailEntries.map(([key, value]) => (
              <div key={key}>
                <span className="font-medium text-gray-900">{labels.exerciseType[key] || key}:</span> {value}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-2 text-sm text-gray-700">None provided</div>
        )}
      </div>
    </div>
  );
}

interface ProgramGenerationChatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** When true, assistant messages are rendered as columns (Session Flow | Purpose | Duration) */
  formatAsSessionStructure?: boolean;
  conversation: AIGenerationConversation | null;
  input: string;
  onInputChange: (value: string) => void;
  busy: boolean;
  onSend: () => void;
  onRestart: () => void;
  onConfirm: () => void;
  canConfirm: boolean;
  confirmLabel?: string;
  restartLabel?: string;
  programPreview?: ProgramCreationPreviewPayload | null;
  phasePreview?: ProgramPhasePreviewPayload | null;
  sessionStructuresPreview?: SessionStructuresPreviewPayload | null;
  workoutPreview?: PhaseWorkoutPreviewPayload | null;
  assignedWorkoutPreview?: AssignedWorkoutEditPreviewPayload | null;
  exerciseNameMap?: Record<string, string>;
  parseError?: string | null;
  inputPlaceholder?: string;
}

export default function ProgramGenerationChatModal({
  open,
  onOpenChange,
  title,
  formatAsSessionStructure = false,
  conversation,
  input,
  onInputChange,
  busy,
  onSend,
  onRestart,
  onConfirm,
  canConfirm,
  confirmLabel = "Confirm and Save",
  restartLabel = "Clear and Start Over",
  programPreview,
  phasePreview,
  sessionStructuresPreview,
  workoutPreview,
  assignedWorkoutPreview,
  exerciseNameMap,
  parseError,
  inputPlaceholder,
}: ProgramGenerationChatModalProps) {
  const { profile } = useAuth();
  const showDebug = Boolean(profile?.debugger_enabled);
  const [rawLogId, setRawLogId] = useState<string | null>(null);
  const [inputsDialog, setInputsDialog] = useState<{
    logId: string;
    rows: GptLogInputSnapshotRow[] | undefined;
  } | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const messages = conversation?.messages ?? [];
  const debugTrace = conversation?.generation_debug_trace ?? [];
  const { orderedLogIds, snapshotByLogId } = useMemo(
    () => collectGptLogDebugInfo(messages),
    [messages],
  );

  useEffect(() => {
    if (open && (conversation?.messages?.length || busy)) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [open, conversation?.messages?.length, busy]);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-0 border border-gray-200 bg-white p-0 text-gray-900 max-w-[90vw] ${
          showDebug ? "sm:max-w-[min(96rem,calc(100vw-1rem))]" : "sm:max-w-[90vw]"
        }`}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0 border-b border-gray-200 px-6 pr-14 py-4">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {title}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {busy && (
                <span className="flex items-center gap-1.5 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </span>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRestart}
                disabled={busy}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {restartLabel}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={onConfirm}
                disabled={busy || !canConfirm}
                className="gap-1.5 bg-primary text-primary-foreground hover:opacity-90"
              >
                <Check className="h-3.5 w-3.5" />
                {confirmLabel}
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            className={`flex min-h-0 flex-1 overflow-hidden ${showDebug ? "lg:flex-row" : "flex-col"}`}
          >
            <div
              className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${showDebug ? "lg:w-1/2" : ""}`}
            >
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <div className="mx-auto w-full max-w-full space-y-4">
              {messages.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  No messages yet. Send a message to refine the AI output.
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isAssistant = msg.role === "assistant";
                  const isLatestMessage = index === messages.length - 1;
                  const parsedSessionStructures =
                    formatAsSessionStructure && isAssistant
                      ? isLatestMessage && sessionStructuresPreview != null
                        ? sessionStructuresPreview
                        : parseSessionStructuresAssistantContent(msg.content)
                      : null;
                  const showMultiSessionStructures =
                    parsedSessionStructures != null &&
                    parsedSessionStructures.session_structures.length > 0;
                  const sessionBlocks =
                    formatAsSessionStructure && isAssistant && !showMultiSessionStructures
                      ? parseSessionStructureBlocks(msg.content)
                      : null;
                  const hasSessionTableBlocks =
                    showMultiSessionStructures ||
                    (sessionBlocks?.some((b) => b.type === "table") ?? false);

                  // Use backend-parsed phasePreview for latest assistant message; otherwise parse locally for all assistant messages
                  const parsedPhases =
                    isAssistant
                      ? (isLatestMessage && phasePreview != null
                          ? { phase1_start_date: phasePreview.phase1_start_date, phases: phasePreview.phases }
                          : parseProgramPhasesAssistantContent(msg.content))
                      : null;
                  const parsedProgram =
                    isAssistant
                      ? (isLatestMessage && programPreview != null
                          ? programPreview
                          : parseProgramCreationAssistantContent(msg.content))
                      : null;
                  const showProgramFormat = parsedProgram != null;
                  const showPhaseFormat = parsedPhases != null;
                  const parsedWorkouts =
                    isAssistant
                      ? (isLatestMessage && workoutPreview != null
                          ? { workouts: workoutPreview.workouts }
                          : parsePhaseWorkoutsAssistantContent(msg.content))
                      : null;
                  const showWorkoutFormat = parsedWorkouts != null;
                  const parsedAssignedWorkout =
                    isAssistant
                      ? (isLatestMessage && assignedWorkoutPreview != null
                          ? assignedWorkoutPreview
                          : parseAssignedWorkoutEditAssistantContent(msg.content))
                      : null;
                  const showAssignedWorkoutFormat = parsedAssignedWorkout != null;

                  return (
                    <div
                      key={msg.id}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`rounded-2xl px-4 py-3 ${
                          msg.role === "user"
                            ? "max-w-[85%] bg-gray-900 text-white"
                            : "w-full max-w-full bg-gray-100 text-gray-900 border border-gray-200"
                        } ${hasSessionTableBlocks || showPhaseFormat ? "min-w-[320px]" : ""}`}
                      >
                        <div className="text-xs font-medium opacity-80 mb-1">
                          {isAssistant ? "Assistant" : "You"}
                        </div>
                        {showMultiSessionStructures && parsedSessionStructures ? (
                          <SessionStructuresFormattedContent
                            items={parsedSessionStructures.session_structures}
                          />
                        ) : formatAsSessionStructure &&
                          sessionBlocks &&
                          sessionBlocks.length > 0 ? (
                          <SessionStructureTables structure={msg.content} />
                        ) : showProgramFormat && parsedProgram ? (
                          <ProgramCreationFormattedContent program={parsedProgram} />
                        ) : showPhaseFormat && parsedPhases ? (
                          <ProgramPhasesFormattedContent
                            phases={parsedPhases.phases}
                            phase1StartDate={parsedPhases.phase1_start_date}
                          />
                        ) : showWorkoutFormat && parsedWorkouts ? (
                          <WorkoutPreviewFormattedContent
                            workouts={parsedWorkouts.workouts}
                            exerciseNameMap={exerciseNameMap}
                          />
                        ) : showAssignedWorkoutFormat && parsedAssignedWorkout ? (
                          <AssignedWorkoutEditFormattedContent
                            workout={parsedAssignedWorkout}
                            exerciseNameMap={exerciseNameMap}
                          />
                        ) : (
                          <div className="whitespace-pre-wrap text-sm leading-relaxed">
                            {msg.content}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              {busy && (
                <div className="flex justify-start">
                  <div className="w-full max-w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-3 text-gray-900">
                    <div className="text-xs font-medium opacity-80 mb-2">
                      Assistant
                    </div>
                    <div className="flex gap-1">
                      <span
                        className="h-2 w-2 rounded-full bg-gray-500 animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      />
                      <span
                        className="h-2 w-2 rounded-full bg-gray-500 animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      />
                      <span
                        className="h-2 w-2 rounded-full bg-gray-500 animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
          </div>

          {showDebug && (
            <div className="flex w-full min-h-0 min-w-0 flex-1 flex-col border-t border-gray-200 bg-gray-100 lg:w-1/2 lg:shrink lg:border-l lg:border-t-0">
              <div className="flex-shrink-0 border-b border-gray-300 bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-900">
                Backend activity
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto border-b border-gray-200 bg-gray-50 p-3">
                {busy && debugTrace.length === 0 ? (
                  <p className="text-xs text-gray-800">Polling… waiting for backend trace lines.</p>
                ) : null}
                {!busy && debugTrace.length === 0 ? (
                  <p className="text-xs text-gray-600">
                    Detailed steps appear here while generation runs when debugger mode is on.
                  </p>
                ) : null}
                {debugTrace.length > 0 ? (
                  <ol className="list-decimal space-y-1 pl-4 text-xs text-gray-900">
                    {debugTrace.map((entry, idx) => (
                      <li
                        key={`${entry.at}-${idx}`}
                        className="marker:text-gray-400"
                      >
                        <span className="font-mono text-[10px] text-gray-500">
                          {entry.at
                            ? `${new Date(entry.at).toLocaleTimeString(undefined, {
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })} `
                            : null}
                        </span>
                        <span className="break-words font-mono">{entry.message}</span>
                      </li>
                    ))}
                  </ol>
                ) : null}
              </div>
              <div className="flex-shrink-0 border-b border-gray-300 bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-900">
                GPT runs
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-3">
                {orderedLogIds.length === 0 ? (
                  <p className="text-xs text-gray-800">IDs appear after the model responds.</p>
                ) : (
                  <ul className="space-y-2">
                    {orderedLogIds.map((id) => (
                      <li
                        key={id}
                        className="flex flex-wrap items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
                      >
                        <button
                          type="button"
                          className="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 font-mono text-xs font-medium text-gray-950 hover:bg-gray-200"
                          onClick={() => setRawLogId(id)}
                        >
                          {id.slice(0, 8)}…
                        </button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() =>
                            setInputsDialog({ logId: id, rows: snapshotByLogId[id] })
                          }
                        >
                          Inputs
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

          {parseError && (
            <div className="flex-shrink-0 border-t border-red-200 bg-red-50 px-6 py-2">
              <div className="w-full rounded-sm text-left text-xs text-red-700">
                {(() => {
                  const aiInputText = lastUserInputForCopy(conversation);
                  const aiOutputText = lastAssistantRawOutputForCopy(conversation);
                  const full = formatParseErrorForClipboard(parseError, {
                    aiInputText,
                    aiOutputText,
                  });
                  return (
                    <>
                <span className="font-medium text-red-800">Parse error:</span>{" "}
                <span className="whitespace-pre-wrap break-words text-red-700">{parseError}</span>
                      <ExpandableCopyDetails
                        copyText={full}
                        aiInputText={aiInputText}
                        aiOutputText={aiOutputText}
                        copyLabel="Click to copy"
                      />
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4">
            <div className="mx-auto flex w-full max-w-full gap-3">
              <Textarea
                value={input}
                onChange={(e) => onInputChange(e.target.value)}
                placeholder={
                  inputPlaceholder ??
                  (assignedWorkoutPreview
                    ? "Describe how you want this workout changed..."
                    : workoutPreview
                    ? "Adjust workout structure, dates, or exercise groupings..."
                    : programPreview
                    ? "Adjust the program focus, difficulty, schedule, or exercise types..."
                    : phasePreview
                    ? "Adjust phase structure or details..."
                    : "Refine the structure...")
                }
                rows={2}
                disabled={busy}
                className="min-h-[72px] resize-none border-gray-300 bg-white text-gray-900 placeholder:text-gray-500 focus-visible:ring-gray-400"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (input.trim()) onSend();
                  }
                }}
              />
              <Button
                onClick={onSend}
                disabled={busy || !input.trim()}
                className="self-end shrink-0 bg-primary text-primary-foreground hover:opacity-90"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
      <GptLogDetailDialog
        open={rawLogId !== null}
        onOpenChange={(next) => {
          if (!next) setRawLogId(null);
        }}
        logId={rawLogId}
      />
      <GptLogInputsDialog
        open={inputsDialog !== null}
        onOpenChange={(next) => {
          if (!next) setInputsDialog(null);
        }}
        logId={inputsDialog?.logId ?? null}
        rows={inputsDialog?.rows}
      />
    </>
  );
}
